import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiX, FiUser, FiUpload } from 'react-icons/fi';
import Toast from './Toast';

import { API_URL, getAssetUrl } from '../config';

function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'staff',
    firstName: '',
    lastName: '',
    officeLocation: '',
  });
  const [officeLocations, setOfficeLocations] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  /** @type {'password' | 'invite'} */
  const [setupMode, setSetupMode] = useState('password');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkSendInvites, setBulkSendInvites] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    loadUsers();
    loadOfficeLocations();
  }, []);

  const loadOfficeLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/locations`);
      setOfficeLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error loading office locations:', error);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (showModal) handleCloseModal();
      else if (showBulkModal) handleCloseBulkModal();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal, showBulkModal]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
      setToast({ message: 'Error loading users', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setFormData({
        username: userToEdit.username,
        email: userToEdit.email,
        password: '',
        role: userToEdit.role,
        firstName: userToEdit.firstName || '',
        lastName: userToEdit.lastName || '',
        officeLocation: userToEdit.officeLocation || '',
      });
      setProfileImage(userToEdit.profileImage);
    } else {
      setEditingUser(null);
      setSetupMode('password');
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'staff',
        firstName: '',
        lastName: '',
        officeLocation: '',
      });
      setProfileImage(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'staff',
      firstName: '',
      lastName: '',
      officeLocation: '',
    });
    setProfileImage(null);
    setSetupMode('password');
  };

  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setBulkFile(null);
    setBulkResult(null);
    setBulkSendInvites(true);
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      setToast({ message: 'Choose a .csv file first.', type: 'error' });
      return;
    }
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('csv', bulkFile);
      fd.append('sendInvites', bulkSendInvites ? 'true' : 'false');
      const response = await axios.post(`${API_URL}/users/bulk-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(response.data);
      setToast({ message: response.data.message || 'Import complete', type: 'success' });
      loadUsers();
    } catch (error) {
      console.error('Bulk import error:', error);
      setToast({ message: error.response?.data?.error || 'Import failed', type: 'error' });
    }
    setBulkLoading(false);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('officeLocation', formData.officeLocation || '');
      if (profileImage && profileImage instanceof File) {
        formDataToSend.append('profileImage', profileImage);
      }

      if (editingUser) {
        if (formData.password.trim()) {
          formDataToSend.append('password', formData.password.trim());
        }
        await axios.put(`${API_URL}/users/${editingUser.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setToast({ message: 'User updated successfully!', type: 'success' });
      } else {
        if (setupMode === 'password' && !formData.password.trim()) {
          setToast({ message: 'Enter a password or switch to “Send invite email”.', type: 'error' });
          return;
        }
        formDataToSend.append('sendInvite', setupMode === 'invite' ? 'true' : 'false');
        if (setupMode === 'password' && formData.password.trim()) {
          formDataToSend.append('password', formData.password.trim());
        }
        const response = await axios.post(`${API_URL}/users`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setToast({
          message: response.data.message || 'User created successfully!',
          type: 'success',
        });
      }
      handleCloseModal();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setToast({ message: error.response?.data?.error || 'Error saving user', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/users/${id}`);
      setToast({ message: 'User deleted successfully!', type: 'success' });
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setToast({ message: 'Error deleting user', type: 'error' });
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const oa = a.officeLocation || '';
      const ob = b.officeLocation || '';
      if (oa !== ob) return oa.localeCompare(ob);
      const na = `${a.lastName || ''} ${a.firstName || ''}`.trim() || a.username;
      const nb = `${b.lastName || ''} ${b.firstName || ''}`.trim() || b.username;
      return na.localeCompare(nb);
    });
  }, [users]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-900 font-semibold">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">User Management</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkResult(null);
                setBulkFile(null);
              }}
              className="flex items-center gap-2 bg-white/80 text-gray-900 border border-gray-300 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl hover:bg-white transition-all duration-200 font-semibold shadow-sm"
            >
              <FiUpload className="text-lg" />
              <span className="hidden sm:inline">Import CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-[#3c79bb] text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl hover:bg-[#306196] transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold"
            >
              <FiPlus className="text-lg" />
              <span className="hidden sm:inline">Add Staff</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        <div className="glass-card rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/20">
              <thead className="glass-light">
                <tr>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">User</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Email</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Office</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Role</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {sortedUsers.map((u) => (
                  <tr key={u.id} className="hover:glass-light transition-all duration-200">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {u.profileImage ? (
                          <img
                            src={getImageUrl(u.profileImage)}
                            alt={u.username}
                            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-white/50"
                          />
                        ) : (
                          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-[#3c79bb] flex items-center justify-center text-white font-semibold text-sm sm:text-base ring-2 ring-white/50">
                            {u.firstName ? u.firstName[0] : u.username[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-sm sm:text-base font-bold text-gray-900">
                            {u.firstName || u.lastName ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : u.username}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-700 font-medium">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm sm:text-base text-gray-800 font-medium">{u.email}</td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium max-w-[10rem] sm:max-w-xs truncate" title={u.officeLocation || ''}>
                      {u.officeLocation ? (
                        <span className="glass px-2 py-1 rounded-lg text-xs font-semibold border border-gray-200/60 bg-white/50">
                          {u.officeLocation}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 sm:px-3 py-1 text-xs font-bold rounded-full ${
                        u.role === 'admin' ? 'glass bg-purple-100/50 text-purple-800 border border-purple-200/50' : 'glass bg-blue-100/50 text-blue-800 border border-blue-200/50'
                      }`}>
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <button
                          onClick={() => handleOpenModal(u)}
                          className="glass text-[#3c79bb] p-2 rounded-xl hover:glass-strong transition-all duration-200 border border-[#3c79bb]/20"
                          title="Edit"
                        >
                          <FiEdit2 className="text-base sm:text-lg" />
                        </button>
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleDelete(u.id)}
                            className="glass text-red-600 p-2 rounded-xl hover:glass-strong transition-all duration-200 border border-red-200"
                            title="Delete"
                          >
                            <FiTrash2 className="text-base sm:text-lg" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showBulkModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseBulkModal}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Import users (CSV)</h2>
              <button
                type="button"
                onClick={handleCloseBulkModal}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleBulkImport} className="p-5 sm:p-6 space-y-4">
              <p className="text-sm text-gray-700">
                Columns: <strong>first name</strong>, <strong>last name</strong>, <strong>email</strong>, and optional{' '}
                <strong>office</strong> (must match an office name from Reporting exactly, case-insensitive). Example header:{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">First name,Last name,Email,Office</code>. Usernames come
                from each email. <strong>Every import is staff</strong> — promote to admin here if needed.
              </p>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">CSV file</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196]"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300"
                  checked={bulkSendInvites}
                  onChange={(e) => setBulkSendInvites(e.target.checked)}
                />
                <span className="text-sm text-gray-800">
                  <span className="font-semibold">Send invite emails</span>
                  <span className="block text-xs text-gray-600 mt-0.5">
                    Each new user receives a link to set their password (SMTP must be configured). If unchecked, accounts
                    are still created with a random password; use Edit user or Forgot password to let them in.
                  </span>
                </span>
              </label>
              <button
                type="submit"
                disabled={bulkLoading}
                className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] font-semibold shadow-lg disabled:opacity-50"
              >
                {bulkLoading ? 'Importing…' : 'Run import'}
              </button>
            </form>
            {bulkResult && (
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-gray-100 space-y-2 text-sm">
                <p className="font-semibold text-gray-900 pt-3">Results</p>
                <p className="text-gray-700">
                  Created: <strong>{bulkResult.created?.length ?? 0}</strong> · Skipped:{' '}
                  <strong>{bulkResult.skipped?.length ?? 0}</strong> · Errors:{' '}
                  <strong>{bulkResult.errors?.length ?? 0}</strong>
                </p>
                {(bulkResult.skipped?.length > 0 || bulkResult.errors?.length > 0) && (
                  <ul className="max-h-40 overflow-y-auto text-xs text-amber-900 bg-amber-50/90 rounded-lg p-2 border border-amber-200 space-y-1">
                    {bulkResult.skipped?.map((s, i) => (
                      <li key={`s-${i}`}>
                        Skipped {s.email}: {s.reason}
                      </li>
                    ))}
                    {bulkResult.errors?.map((er, i) => (
                      <li key={`e-${i}`}>
                        Line {er.line} ({er.email}): {er.error}
                      </li>
                    ))}
                  </ul>
                )}
                {bulkResult.created?.some((c) => c.inviteError) && (
                  <p className="text-xs text-red-700">
                    Some accounts were created but the invite email failed; check SMTP log in Settings.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {editingUser ? 'Edit User' : 'Add New Staff'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div className="flex items-center gap-6 mb-6">
                <div className="flex-shrink-0">
                  {profileImage ? (
                    <img
                      src={profileImage instanceof File ? URL.createObjectURL(profileImage) : getImageUrl(profileImage)}
                      alt="Profile"
                      className="h-24 w-24 rounded-full object-cover ring-4 ring-white/50 shadow-lg"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-3xl font-semibold">
                      <FiUser />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Profile Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196] file:shadow-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>

              {!editingUser && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-3">
                  <p className="text-sm font-bold text-gray-900">How should they sign in?</p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="setupMode"
                      className="mt-1"
                      checked={setupMode === 'password'}
                      onChange={() => setSetupMode('password')}
                    />
                    <span>
                      <span className="font-semibold text-gray-900">Admin sets password</span>
                      <span className="block text-xs text-gray-600 mt-0.5">Enter a temporary or permanent password below.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="setupMode"
                      className="mt-1"
                      checked={setupMode === 'invite'}
                      onChange={() => setSetupMode('invite')}
                    />
                    <span>
                      <span className="font-semibold text-gray-900">Send invite email</span>
                      <span className="block text-xs text-gray-600 mt-0.5">
                        They get a link to choose their password (requires SMTP enabled in Settings).
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {(editingUser || setupMode === 'password') && (
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser && setupMode === 'password'}
                    autoComplete="new-password"
                    className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium"
                  >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Office location</label>
                <select
                  value={formData.officeLocation}
                  onChange={(e) => setFormData({ ...formData, officeLocation: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium"
                >
                  <option value="">None</option>
                  {officeLocations.map((loc) => (
                    <option key={loc.id} value={loc.name}>
                      {loc.name}
                    </option>
                  ))}
                  {formData.officeLocation &&
                    !officeLocations.some((l) => l.name === formData.officeLocation) && (
                      <option value={formData.officeLocation}>
                        {formData.officeLocation} (update — not in Reporting list)
                      </option>
                    )}
                </select>
                <p className="text-xs text-gray-500 mt-1.5">
                  Same offices as Reporting. Used for sorting and future features.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-bold border border-gray-300 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
